"use client";

import Link from "next/link";
import { ArrowRight } from "@/components/icons";
import { HeroGlow } from "@/components/ui";
import { buttonVariants } from "@/components/ui/button";
import SectionHeader from "@/components/ui/section-header";
import FaqSection from "@/components/sections/products/shared/faq";
import { linkifyFirstMatch } from "@/components/sections/products/shared/faq/linkify";
import {
  ProConsColumn,
  type ProConsItem,
} from "@/components/sections/editorial/proxy-server/_shared/pros-cons-column";
import { APP_URL, EMAIL, REGISTER_URL } from "@/lib/constants";
import {
  PROGRAM_FACTS,
  HOW_IT_WORKS_STEPS,
  COMMISSION_TIERS,
  EARNINGS_EXAMPLES,
  PAYOUT_DETAILS,
  PROMOTION_APPROPRIATE,
  PROMOTION_RESTRICTED,
  PROGRAM_VS_PARTNER,
  REFERRAL_FAQS,
} from "./data";

const TERMS_HREF = "/referral-program-terms-of-use";

const LINK_TARGETS = [{ phrase: "Referral Program Terms of Use", href: TERMS_HREF }];
const faqs = REFERRAL_FAQS.map((faq) => ({
  ...faq,
  a: linkifyFirstMatch(faq.a, LINK_TARGETS),
}));

const PROMOTION_GOOD: ProConsItem[] = PROMOTION_APPROPRIATE.map((body, i) => ({
  label: `Example ${i + 1}`,
  body,
}));
const PROMOTION_BAD: ProConsItem[] = PROMOTION_RESTRICTED.map((body, i) => ({
  label: `Rule ${i + 1}`,
  body,
}));

export default function ReferralProgramSection() {
  return (
    <main id="main-content" tabIndex={-1}>
      {/* ───────────────────────── Hero ───────────────────────── */}
      <section
        aria-labelledby="referral-hero-heading"
        className="border-border relative overflow-y-clip border-b pt-24 pb-16 sm:pt-32"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <HeroGlow align="center" />
        </div>

        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-brand-green mb-4 text-xs font-semibold tracking-widest uppercase">
            Referral Program
          </p>

          <h1
            id="referral-hero-heading"
            className="text-foreground text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Earn Up to 40% With the Proxidize Referral Program
          </h1>

          <p className="text-muted-foreground light:text-foreground/80 mx-auto mt-5 max-w-2xl text-lg leading-relaxed">
            Earn 20% to 40% commission recommending Proxidize, plus a 20% coupon for new customers —
            up to $10,000 per qualifying referral.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              href={REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: "default", size: "lg" })}
            >
              Open Proxidize to Sign Up
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: "secondary", size: "lg" })}
            >
              Sign In to Get Your Referral Link
            </Link>
            <Link
              href={TERMS_HREF}
              className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-4"
            >
              View Program Terms
            </Link>
          </div>
        </div>

        {/* Program facts */}
        <div className="relative mx-auto mt-14 max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="border-border bg-card overflow-x-auto rounded-2xl border">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-border border-b">
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">
                    Program benefit
                  </th>
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">Current offer</th>
                </tr>
              </thead>
              <tbody>
                {PROGRAM_FACTS.map((row) => (
                  <tr key={row.label} className="border-border/60 border-b last:border-b-0">
                    <td className="text-foreground px-5 py-3 font-semibold sm:px-6">{row.label}</td>
                    <td className="text-muted-foreground px-5 py-3 sm:px-6">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ───────────────────────── Quick Answer ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-quick-answer-heading"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-brand-green mb-2 text-sm font-medium">Quick Answer</p>
          <h2
            id="referral-quick-answer-heading"
            className="text-foreground mb-4 scroll-mt-40 text-2xl font-bold tracking-tight sm:text-3xl"
          >
            What is the Proxidize Referral Program?
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            The Proxidize proxy referral program lets eligible affiliates earn commission by
            referring new customers. The Standard rate is 20%, while eligible Silver and Gold
            affiliates can earn 30% and 40%. Referred customers receive a 20% coupon, referrals are
            tracked for 30 days, and an affiliate can earn up to $10,000 from each qualifying
            referral.
          </p>
        </div>
      </section>

      {/* ───────────────────────── How It Works ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-how-it-works-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionHeader
              eyebrow="Getting Started"
              id="referral-how-it-works-heading"
              heading="How the Proxidize Referral Program Works"
              headingClassName="scroll-mt-40"
            />
          </div>

          <ol className="mt-8 grid list-none grid-cols-1 gap-4 sm:grid-cols-3">
            {HOW_IT_WORKS_STEPS.map((step, i) => (
              <li
                key={step.title}
                className="border-border bg-card flex flex-col gap-3 rounded-2xl border p-6"
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums"
                  style={{
                    background: "color-mix(in srgb, var(--brand-green) 8%, transparent)",
                    color: "var(--brand-green)",
                    border: "1px solid color-mix(in srgb, var(--brand-green) 20%, transparent)",
                  }}
                >
                  {i + 1}
                </span>
                <span className="text-foreground text-sm font-semibold">{step.title}</span>
                <span className="text-muted-foreground text-sm leading-relaxed">{step.body}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ───────────────────────── Commission Tiers ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-commission-tiers-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionHeader
              eyebrow="Commission"
              id="referral-commission-tiers-heading"
              heading="Referral Commission Tiers"
              body="Every affiliate starts with the Standard rate. Eligible affiliates may progress to Silver or Gold as their referral performance grows. Tier changes are subject to Proxidize approval."
              headingClassName="scroll-mt-40"
            />
          </div>

          <div className="border-border bg-card mt-8 overflow-x-auto rounded-2xl border">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-border border-b">
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">Tier</th>
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">
                    Commission rate
                  </th>
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">How it works</th>
                </tr>
              </thead>
              <tbody>
                {COMMISSION_TIERS.map((row) => (
                  <tr key={row.tier} className="border-border/60 border-b last:border-b-0">
                    <td className="text-foreground px-5 py-3 font-semibold sm:px-6">{row.tier}</td>
                    <td className="text-muted-foreground px-5 py-3 tabular-nums sm:px-6">
                      {row.rate}
                    </td>
                    <td className="text-muted-foreground px-5 py-3 leading-relaxed sm:px-6">
                      {row.how}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            Your assigned commission rate should be confirmed through your Proxidize account or
            direct communication from Proxidize. Program eligibility and commission payments remain
            subject to the{" "}
            <Link
              href={TERMS_HREF}
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              Referral Program Terms of Use
            </Link>
            .
          </p>
        </div>
      </section>

      {/* ───────────────────────── Worked Earnings Examples ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-earnings-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionHeader
              eyebrow="Cost Examples"
              id="referral-earnings-heading"
              heading="How Much Can You Earn?"
              body="The following examples show the commission generated by different qualifying customer payments at each published rate."
              headingClassName="scroll-mt-40"
            />
          </div>

          <div className="border-border bg-card mt-8 overflow-x-auto rounded-2xl border">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-border border-b">
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">
                    Qualifying customer payment
                  </th>
                  <th className="text-foreground px-5 py-3 text-right font-semibold sm:px-6">
                    20% Standard
                  </th>
                  <th className="text-foreground px-5 py-3 text-right font-semibold sm:px-6">
                    30% Silver
                  </th>
                  <th className="text-foreground px-5 py-3 text-right font-semibold sm:px-6">
                    40% Gold
                  </th>
                </tr>
              </thead>
              <tbody>
                {EARNINGS_EXAMPLES.map((row) => (
                  <tr key={row.payment} className="border-border/60 border-b last:border-b-0">
                    <td className="text-foreground px-5 py-3 font-semibold sm:px-6">
                      {row.payment}
                    </td>
                    <td className="text-muted-foreground px-5 py-3 text-right tabular-nums sm:px-6">
                      {row.standard}
                    </td>
                    <td className="text-muted-foreground px-5 py-3 text-right tabular-nums sm:px-6">
                      {row.silver}
                    </td>
                    <td className="text-muted-foreground px-5 py-3 text-right tabular-nums sm:px-6">
                      {row.gold}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-muted-foreground mt-4 text-xs leading-relaxed">
            These are illustrative calculations, not guaranteed earnings. Commission is calculated
            on qualifying amounts under the program rules and excludes applicable taxes, fees,
            refunds, and non-qualifying transactions. Total commission is limited to $10,000 per
            qualifying referral.
          </p>
        </div>
      </section>

      {/* ───────────────────────── Coupon ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-coupon-heading"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Customer Coupon"
            id="referral-coupon-heading"
            heading="Give New Customers 20% Off"
            headingClassName="scroll-mt-40"
          />
          <p className="text-muted-foreground mt-4 text-base leading-relaxed">
            Each affiliate receives a 20% Proxidize coupon to share with eligible new customers. The
            coupon gives customers an additional reason to try Proxidize while allowing the purchase
            to be associated with the affiliate&apos;s referral activity.
          </p>
          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            Use the referral link and coupon only through approved channels. Do not publish the
            coupon on coupon websites, discount aggregators, or other channels prohibited by the
            program rules.
          </p>
        </div>
      </section>

      {/* ───────────────────────── Tracking and Payouts ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-tracking-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionHeader
              eyebrow="Tracking & Payouts"
              id="referral-tracking-heading"
              heading="Referral Tracking and Payouts"
              headingClassName="scroll-mt-40"
            />
            <p className="text-muted-foreground mt-4 text-base leading-relaxed">
              Proxidize uses your unique referral link to associate eligible customers with your
              account. The referral tracking period lasts 30 days from the customer&apos;s click.
            </p>
            <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
              A referral may not be recorded if the customer rejects the required cookie, deletes
              it, purchases after it expires, uses a different browser or device, or otherwise
              cannot be associated with the referral link.
            </p>
          </div>

          <div className="border-border bg-card mt-8 overflow-x-auto rounded-2xl border">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-border border-b">
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">Payout detail</th>
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">
                    Current program condition
                  </th>
                </tr>
              </thead>
              <tbody>
                {PAYOUT_DETAILS.map((row) => (
                  <tr key={row.label} className="border-border/60 border-b last:border-b-0">
                    <td className="text-foreground px-5 py-3 font-semibold sm:px-6">{row.label}</td>
                    <td className="text-muted-foreground px-5 py-3 leading-relaxed sm:px-6">
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            Eligible balances are processed monthly once the $100 minimum and applicable waiting
            period have been satisfied. Affiliates are responsible for maintaining accurate payout
            information.
          </p>
        </div>
      </section>

      {/* ───────────────────────── Who Can Join ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-who-can-join-heading"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Eligibility"
            id="referral-who-can-join-heading"
            heading="Who Can Join the Referral Program?"
            headingClassName="scroll-mt-40"
          />
          <p className="text-muted-foreground mt-4 text-base leading-relaxed">
            The program can fit existing Proxidize customers, developers, agencies, consultants,
            educators, publishers, creators, and other professionals whose audiences may benefit
            from reliable proxy infrastructure.
          </p>
          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            Participants must provide accurate account and payout information, describe Proxidize
            honestly, disclose the affiliate relationship, and comply with applicable advertising,
            privacy, data-protection, and anti-spam requirements.
          </p>
          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            The Referral Program does not give an affiliate permission to resell, sublicense, or
            redistribute Proxidize services.
          </p>
        </div>
      </section>

      {/* ───────────────────────── Promotion Rules ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-promotion-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionHeader
              eyebrow="Promotion Rules"
              id="referral-promotion-heading"
              heading="How Can You Promote Your Referral Link?"
              body="Affiliates can recommend Proxidize through relevant, transparent content and direct recommendations to appropriate audiences."
              headingClassName="scroll-mt-40"
            />
          </div>

          <div className="mt-8 grid gap-2 sm:gap-4 md:grid-cols-2">
            <ProConsColumn heading="Appropriate promotion" items={PROMOTION_GOOD} positive />
            <ProConsColumn
              heading="Restricted or not permitted"
              items={PROMOTION_BAD}
              positive={false}
            />
          </div>

          <p className="text-muted-foreground mt-6 text-sm leading-relaxed">
            Review the complete{" "}
            <Link
              href={TERMS_HREF}
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              Referral Program Terms of Use
            </Link>{" "}
            before distributing your referral link.
          </p>
        </div>
      </section>

      {/* ───────────────────────── Referral vs. Partner Program ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-vs-partner-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionHeader
              eyebrow="Not a Reseller Program"
              id="referral-vs-partner-heading"
              heading="Referral Program vs. Partner Program"
              body="The Referral Program is for people and organizations recommending Proxidize through a tracked link. The Partner Program covers broader reseller, integration, and strategic partnership arrangements."
              headingClassName="scroll-mt-40"
            />
          </div>

          <div className="border-border bg-card mt-8 overflow-x-auto rounded-2xl border">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-border border-b">
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">Program</th>
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">Purpose</th>
                  <th className="text-foreground px-5 py-3 font-semibold sm:px-6">
                    Current availability
                  </th>
                </tr>
              </thead>
              <tbody>
                {PROGRAM_VS_PARTNER.map((row) => (
                  <tr key={row.program} className="border-border/60 border-b last:border-b-0">
                    <td className="text-foreground px-5 py-3 font-semibold sm:px-6">
                      {row.program}
                    </td>
                    <td className="text-muted-foreground px-5 py-3 leading-relaxed sm:px-6">
                      {row.purpose}
                    </td>
                    <td className="text-muted-foreground px-5 py-3 leading-relaxed sm:px-6">
                      {row.availability}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            Joining the Referral Program does not grant reseller or integration-partner rights.
          </p>
        </div>
      </section>

      {/* ───────────────────────── FAQ ───────────────────────── */}
      <FaqSection
        faqs={faqs}
        heading="Frequently Asked Questions"
        description="Quick answers about commission, tracking, payouts, and participation in the Proxidize Referral Program."
        headingId="referral-faq-heading"
        headingClassName="scroll-mt-40"
        hideContactLink
      />

      {/* ───────────────────────── Terms Summary ───────────────────────── */}
      <section
        className="py-section-sm border-border border-b"
        aria-labelledby="referral-terms-summary-heading"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="border-border bg-muted/20 rounded-2xl border p-6 sm:p-8">
            <h2
              id="referral-terms-summary-heading"
              className="text-foreground mb-3 scroll-mt-40 text-xl font-bold tracking-tight sm:text-2xl"
            >
              Important Program Information
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Referral attribution, commission eligibility, tier assignments, payments, customer
              eligibility, permitted promotion methods, refunds, termination, and other conditions
              are governed by the Proxidize Referral Program Terms of Use.
            </p>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
              Review the complete terms before distributing your referral link.
            </p>
            <Link
              href={TERMS_HREF}
              className={`${buttonVariants({ variant: "secondary", size: "default" })} mt-5`}
            >
              Read the Referral Program Terms of Use
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────────────────── Final CTA ───────────────────────── */}
      <section className="py-section" aria-labelledby="referral-final-cta-heading">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2
            id="referral-final-cta-heading"
            className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            Start Earning With Proxidize
          </h2>
          <p className="text-muted-foreground mb-8 text-base leading-relaxed">
            Create a Proxidize account to access your referral link and 20% customer coupon.
            Recommend Proxidize through approved channels and earn 20% to 40% on qualifying
            subscriptions and renewals.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              href={REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: "default", size: "lg" })}
            >
              Open Proxidize to Sign Up
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: "secondary", size: "lg" })}
            >
              Sign In to Your Proxidize Account
            </Link>
          </div>
          <p className="text-muted-foreground mt-6 text-sm">
            Questions about the program? Reach our team at{" "}
            <a
              href={`mailto:${EMAIL.support}`}
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              {EMAIL.support}
            </a>
            .
          </p>
        </div>
      </section>
    </main>
  );
}
